import { redirect, type LoaderFunctionArgs } from "react-router";
import { Link } from "react-router";
import { BRAND, PAGE_TITLE, SITE_ORIGIN } from "../brand";
import {
  APP_STORE_URL,
  marketingLinks,
  MarketingShell,
  socialImageMeta,
} from "../components/marketing/layout";
import { NutritionPanelMotif } from "../components/marketing/NutritionPanelMotif";
import {
  ArrowRight,
  CheckCircle,
  Download,
  Leaf,
  Package,
  Pill,
  Scale,
  Search,
  Store,
} from "../components/marketing/icons";

// The app_url root is where Shopify opens the embedded iframe
// (`/?shop=<shop>&host=<host>&embedded=1`). Bootstrap into the embedded admin so
// App Bridge loads (shouldLoadAppBridge matches `/app*`) and the app renders.
// Without this route, `/` has no match and the embedded entry 404s (the embed
// shows nothing / a broken iframe). Direct visits without `shop` get the landing.
export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  if (url.searchParams.get("shop")) {
    return redirect(`/app?${url.searchParams.toString()}`);
  }
  return null;
};

export const links = marketingLinks;

const FAQS = [
  {
    q: "Is EasyNutriFact really free?",
    a: "Yes. Every feature — Nutrition Facts and Supplement Facts panels, the USDA lookup, the 300 DPI export — is free. There is no paid tier, no trial clock, and no per-label limit.",
  },
  {
    q: "Are the labels compliant with FDA rules?",
    a: "The panels follow the FDA's Nutrition Facts format (21 CFR 101.9) and Supplement Facts format (21 CFR 101.36), and the app applies the FDA's rounding rules and % Daily Value math automatically. What the app cannot do is verify your numbers: it formats and rounds the values you provide. Always have a qualified labeling expert verify the final label before printing.",
  },
  {
    q: "Where do the nutrition numbers come from?",
    a: "From you. Most merchants type values from a lab analysis or a recipe calculation. When you don't have numbers in hand, the built-in USDA FoodData Central lookup can prefill per-serving nutrient values from your ingredients as a starting point — not a substitute for verified data.",
  },
  {
    q: "Where is my label stored?",
    a: "On your own Shopify store, as a metaobject attached to the product. You own the data, you can see it in Shopify admin under Content → Metaobjects, and an in-app control removes everything in one action.",
  },
  {
    q: "What can I do with the exported PNG?",
    a: "The export is a 300 DPI white-background PNG — print resolution, identical to the live preview. Use it in packaging artwork, on the product page, or in marketplace listings that ask for a label image.",
  },
];

export const meta = () => [
  { title: PAGE_TITLE },
  {
    name: "description",
    content:
      "Build FDA-style Nutrition and Supplement Facts panels inside Shopify admin: live preview, automatic rounding, USDA lookup, print-ready PNG export.",
  },
  { name: "robots", content: "index,follow" },
  { tagName: "link", rel: "canonical", href: `${SITE_ORIGIN}/` },
  { property: "og:url", content: `${SITE_ORIGIN}/` },
  { property: "og:type", content: "website" },
  {
    property: "og:title",
    content: "EasyNutriFact — Nutrition & Supplement Facts labels for Shopify",
  },
  {
    property: "og:description",
    content:
      "FDA-style panels with automatic 21 CFR rounding, USDA lookup, and 300 DPI print-ready export — inside Shopify admin. Free.",
  },
  ...socialImageMeta(),
  // SoftwareApplication is the node an answer engine reads to learn this is an app,
  // who publishes it, and what it costs. Keep it FIRST: test/crawlability.test.ts
  // finds the first script:ld+json entry.
  {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: BRAND.name,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web (Shopify admin)",
      url: `${SITE_ORIGIN}/`,
      installUrl: APP_STORE_URL,
      description:
        "Build FDA-style Nutrition Facts (21 CFR 101.9) and Supplement Facts (21 CFR 101.36) " +
        "panels inside Shopify admin, with automatic FDA rounding, % Daily Values, USDA " +
        "FoodData Central lookup, and 300 DPI print-ready PNG export.",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      publisher: { "@type": "Organization", name: "Legitimate LLC" },
    },
  },
  {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  },
];

export default function Index() {
  return (
    <MarketingShell>
      {/* ---- Hero ---- */}
      <section className="nm-hero">
        <div className="nm-wrap nm-hero__grid">
          <div>
            <p className="nm-eyebrow nm-reveal" data-d="1">
              For food, beverage &amp; supplement brands
            </p>
            <h1 className="nm-reveal" data-d="2">
              The label panel every American can read, <em>built in your admin.</em>
            </h1>
            <p className="nm-hero__lede nm-reveal" data-d="3">
              EasyNutriFact turns your numbers into a print-ready, FDA-style{" "}
              <strong>Nutrition Facts</strong> or <strong>Supplement Facts</strong>{" "}
              panel — live as you type, with 21 CFR rounding and % Daily Values
              applied automatically. Save it to the product, export a 300 DPI PNG,
              done. Free.
            </p>
            <div className="nm-hero__cta nm-reveal" data-d="4">
              <a className="nm-btn" href={APP_STORE_URL}>
                Install free on Shopify
                <ArrowRight />
              </a>
              <Link className="nm-btn nm-btn--ghost" to="/demo">
                Try the live demo
              </Link>
            </div>
            <div className="nm-hero__note nm-reveal" data-d="5">
              <span>
                <CheckCircle />
                Free — no tiers, no trial clock
              </span>
              <span>
                <CheckCircle />
                21 CFR 101.9 rounding built in
              </span>
              <span>
                <CheckCircle />
                USDA FoodData Central lookup
              </span>
            </div>
          </div>
          <div className="nm-reveal" data-d="3">
            <NutritionPanelMotif />
          </div>
        </div>
      </section>

      {/* ---- Trust strip ---- */}
      <section className="nm-section--tight">
        <div className="nm-wrap">
          <div className="nm-trust">
            <span className="nm-trust__label">Built for</span>
            <span className="nm-tag">Snack &amp; pantry brands</span>
            <span className="nm-tag">Beverage &amp; brewing</span>
            <span className="nm-tag">Supplement makers</span>
            <span className="nm-tag">Farmers-market growers going retail</span>
            <span className="nm-tag">Meal-prep &amp; baked goods</span>
          </div>
        </div>
      </section>

      {/* ---- How it works ---- */}
      <section className="nm-section nm-band">
        <div className="nm-wrap">
          <div className="nm-head nm-head--center">
            <p className="nm-eyebrow nm-eyebrow--center">How it works</p>
            <h2>From recipe numbers to a print-ready panel in minutes</h2>
            <p>
              No label software, no designer, no spreadsheet of rounding rules.
              Three steps inside the Shopify admin you already live in.
            </p>
          </div>
          <ol className="nm-steps" style={{ listStyle: "none", padding: 0 }}>
            <li className="nm-step">
              <span className="nm-step__n" />
              <span className="nm-step__icon">
                <Package />
              </span>
              <h3>Pick a product</h3>
              <p>
                Open EasyNutriFact from Shopify admin and choose the product
                you&rsquo;re labeling. The editor loads with every nutrient field
                the US panel expects.
              </p>
            </li>
            <li className="nm-step">
              <span className="nm-step__n" />
              <span className="nm-step__icon">
                <Search />
              </span>
              <h3>Enter or look up values</h3>
              <p>
                Type your lab or recipe numbers — or search USDA FoodData Central
                to prefill from your ingredients. The panel rebuilds live with
                FDA rounding on every keystroke.
              </p>
            </li>
            <li className="nm-step">
              <span className="nm-step__n" />
              <span className="nm-step__icon">
                <Download />
              </span>
              <h3>Save &amp; export</h3>
              <p>
                Save the panel to the product as a Shopify metaobject, then
                export a 300 DPI print-ready PNG for packaging, the product
                page, or marketplace listings.
              </p>
            </li>
          </ol>
        </div>
      </section>

      {/* ---- What you get ---- */}
      <section className="nm-section">
        <div className="nm-wrap">
          <div className="nm-head">
            <p className="nm-eyebrow">What you get</p>
            <h2>A labeling desk, not a design tool</h2>
            <p>
              Everything that makes a Nutrition Facts panel tedious — the format,
              the rounding, the %DV math — is handled by the app. You bring the
              numbers.
            </p>
          </div>
          <div className="nm-cards" style={{ marginTop: "2.6rem" }}>
            <article className="nm-card">
              <span className="nm-card__icon">
                <Leaf />
              </span>
              <h3>Nutrition Facts panels</h3>
              <p>
                The standard US panel (21 CFR 101.9): serving size, calories, the
                full macro list, and the four mandatory micronutrients — in the
                format shoppers already know how to read.
              </p>
              <p className="nm-card__eg">
                <b>Example</b>
                2/3 cup granola, 230 calories — the FDA&rsquo;s own sample is the
                demo&rsquo;s starting point.
              </p>
            </article>
            <article className="nm-card">
              <span className="nm-card__icon">
                <Pill />
              </span>
              <h3>Supplement Facts panels</h3>
              <p>
                The 21 CFR 101.36 format for dietary supplements: dosage-form
                servings, dietary ingredients, automatic %DV, and proprietary
                blends with dagger footnotes.
              </p>
              <p className="nm-card__eg">
                <b>Example</b>
                &ldquo;2 capsules&rdquo; servings with a proprietary blend listed
                under a &dagger; footnote.
              </p>
            </article>
            <article className="nm-card">
              <span className="nm-card__icon">
                <Scale />
              </span>
              <h3>Rounding you don&rsquo;t audit</h3>
              <p>
                Every increment and &ldquo;less than&rdquo; threshold from 21 CFR
                101.9 is applied by the engine — nearest 5 or 10 calories,
                0.5 g fat steps, the 2/5/10 %DV grid for micronutrients.
              </p>
              <p className="nm-card__eg">
                <b>Example</b>
                4.75 g saturated fat shows as 24% DV — computed from the
                unrounded value, as the rule requires.
              </p>
            </article>
            <article className="nm-card">
              <span className="nm-card__icon">
                <Search />
              </span>
              <h3>USDA ingredient lookup</h3>
              <p>
                Search USDA FoodData Central from inside the editor and prefill
                per-serving nutrient values from your recipe&rsquo;s ingredients —
                a starting point when lab numbers aren&rsquo;t in hand.
              </p>
              <p className="nm-card__eg">
                <b>Example</b>
                &ldquo;Rolled oats&rdquo; → calories, fiber, sugars per 100 g,
                scaled to your serving.
              </p>
            </article>
            <article className="nm-card">
              <span className="nm-card__icon">
                <Download />
              </span>
              <h3>300 DPI print-ready PNG</h3>
              <p>
                One click exports the exact panel you previewed — white
                background, print resolution — for packaging artwork, product
                pages, Amazon, TikTok Shop, and Walmart.
              </p>
              <p className="nm-card__eg">
                <b>Example</b>
                What you see in the editor is what the printer gets — preview and
                export are the same renderer.
              </p>
            </article>
            <article className="nm-card">
              <span className="nm-card__icon">
                <Store />
              </span>
              <h3>Saved on your own store</h3>
              <p>
                Labels live as Shopify metaobjects attached to each product —
                visible in Shopify admin under Content → Metaobjects, removable
                in one action, never hostage to us.
              </p>
              <p className="nm-card__eg">
                <b>Example</b>
                Uninstall, reinstall — your labels are still on your store where
                you left them.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* ---- Honest framing (the disclaimer must survive) ---- */}
      <section className="nm-section--tight">
        <div className="nm-wrap nm-wrap--narrow">
          <div className="nm-callout nm-callout--warn">
            <p>
              <strong>Honest framing.</strong> EasyNutriFact formats and rounds
              the values you provide — it doesn&rsquo;t test recipes or guarantee
              regulatory compliance. Always have a qualified labeling expert
              verify the final label before printing.
            </p>
          </div>
        </div>
      </section>

      {/* ---- FAQ ---- */}
      <section className="nm-section nm-band">
        <div className="nm-wrap nm-wrap--narrow">
          <div className="nm-head nm-head--center">
            <p className="nm-eyebrow nm-eyebrow--center">Questions</p>
            <h2>Asked before install</h2>
          </div>
          <div className="nm-faq">
            {FAQS.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <div className="nm-faq__body">{f.a}</div>
              </details>
            ))}
          </div>
          <p style={{ marginTop: "1.6rem", textAlign: "center" }}>
            <Link className="nm-textlink" to="/support">
              More answers on the support page
              <ArrowRight width={16} height={16} />
            </Link>
          </p>
        </div>
      </section>

      {/* ---- Guide bridge ---- */}
      <section className="nm-section--tight">
        <div className="nm-wrap nm-wrap--narrow" style={{ textAlign: "center" }}>
          <p className="nm-eyebrow nm-eyebrow--center">Doing it by hand?</p>
          <h2 style={{ fontSize: "var(--step-2)", marginTop: "1rem" }}>
            Read the rounding rules the app applies for you
          </h2>
          <p style={{ marginTop: "0.9rem", color: "var(--ink-600)" }}>
            Our long-form guide walks a label from serving-size RACC to the final
            footnote — every increment and Daily Value table included.
          </p>
          <p style={{ marginTop: "1.2rem" }}>
            <Link className="nm-textlink" to="/how-to-build-an-fda-nutrition-facts-label">
              How to build an FDA Nutrition Facts label
              <ArrowRight width={16} height={16} />
            </Link>
          </p>
        </div>
      </section>

      {/* ---- Closing CTA ---- */}
      <section className="nm-section">
        <div className="nm-wrap">
          <div className="nm-cta nm-reveal" data-d="1">
            <p className="nm-eyebrow nm-eyebrow--center" style={{ color: "var(--leaf-300)" }}>
              Free forever
            </p>
            <h2>Your next product ships with a proper label</h2>
            <p>
              Install EasyNutriFact, pick a product, and watch the panel build
              itself as you type. No account setup, no card, no catch.
            </p>
            <div className="nm-cta__actions">
              <a className="nm-btn" href={APP_STORE_URL}>
                Install free on Shopify
                <ArrowRight />
              </a>
              <Link className="nm-btn nm-btn--ghost" to="/demo">
                Try the demo first
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
